/* eslint-disable eslint-comments/disable-enable-pair */
/* eslint-disable jsx-a11y/no-static-element-interactions */
/* eslint-disable jsx-a11y/click-events-have-key-events */
import { CircularProgress } from '@material-ui/core';
import { createStyles, makeStyles } from '@material-ui/core/styles';
import HelpOutlineIcon from '@material-ui/icons/HelpOutline';
import { ExplorerUrl } from '@mogul/shared';
import { BigNumber } from 'ethers/lib/ethers';
import React, { useContext, useEffect, useState } from 'react';
import toast, { Toaster } from 'react-hot-toast';
import { useAccount, useBalance } from 'wagmi';

import { ReactComponent as XIcon } from '../../../../common/svg/general/icon-x.svg';
import bnbStakingLogo from '../../../../common/svg/starsStaking/bnbStaking.svg';
import starsStakingLogo from '../../../../common/svg/starsStaking/starsStaking.svg';
import { validPositiveNumber } from '../../../../constants/regexs';
import { CoinGeckoContext } from '../../../../contexts/app-state-context/coingecko/coingecko.context';
import { useExplorerUrl, useNetwork } from '../../../../hooks';
import { useStarsEthLpTokenContract } from '../../../../hooks/lp-token-contract.hooks';
import { useStarsContract } from '../../../../hooks/stars-contract.hooks';
import { useStarStakingContract } from '../../../../hooks/stars-staking.hook';
import { WEB3_CONFIG } from '../../../../lib';
import {
  formatEther,
  getNetworkCurrencySymbol,
  parseEther,
  STARS_STAKING_Address,
} from '../../../../lib/smart-contract-utils';
import { Button } from '../../../core/button/Button';
import { Tooltip } from '../../../core/tooltip';
import { ModalFinishState } from './ModalFinishState.component';
import styles from './StakeModal.module.css';

const useStyles = makeStyles(() =>
  createStyles({
    loader: {
      color: 'white',
    },
  }),
);

export interface StakingFormValues {
  tokenAmount?: string;
}

enum ModalState {
  buy = 'BUY',
  finished = 'FINISHED',
}
export interface StakingModalProps {
  poolId: number;
  poolName: string;
  poolDuration: number;
  lockupPeriod: number;
  apr: string;
  lpUSD: number;
  lpTotalSupply: number;
}

export interface Staking extends StakingModalProps {
  onClose: () => void;
}

export const StakingModal: React.FC<Staking> = props => {
  const classes = useStyles();
  const { address } = useAccount();
  const { chainKey } = useNetwork();
  const { starsUsdRate } = useContext(CoinGeckoContext);
  const { deposit } = useStarStakingContract();
  const [value, setValue] = useState('0');
  const [lpBalance, setLpBalance] = useState('0');
  const [percentage, setPercentage] = useState('0');
  const [submitting, setSubmitting] = useState(false);
  const [modalState, setModalState] = useState(ModalState.buy);
  const [transactionUrl, setTransactionUrl] = useState('');
  const [approved, setApproved] = useState(false);
  const explorerUrl = useExplorerUrl();

  const starsBalance = useBalance({
    address,
    token: WEB3_CONFIG.contracts[chainKey].stars,
  });

  const { onClose } = props;

  const {
    initialized: starsInitialized,
    allowance,
    approveInfinite,
  } = useStarsContract();

  const {
    initialized: LpInitialized,
    allowance: lpAllowance,
    approveInfinite: lpApproveInfinite,
    getBalance,
  } = useStarsEthLpTokenContract();

  const isStarsLoading =
    (starsBalance.isFetching && starsBalance.fetchStatus !== 'idle') ||
    starsBalance.isLoading;

  const isLpPool = props.poolName === 'The Oscar’s Pool';

  const handleClose = () => {
    if (submitting) {
      return;
    }

    onClose();
  };

  const handleApprove = async (e: React.FormEvent<HTMLButtonElement>) => {
    e.preventDefault();

    try {
      setSubmitting(true);

      const action = isLpPool ? lpApproveInfinite : approveInfinite;
      const isApproved = await action(
        WEB3_CONFIG.contracts[chainKey].starsStaking,
      );

      setApproved(isApproved);
    } catch (error) {
      toast.error('failed to approve staking contract');
    } finally {
      setSubmitting(false);
    }
  };

  // price ratios

  const handleStakingAmount = (event: React.FormEvent<HTMLInputElement>) => {
    if (
      !starsBalance.data ||
      !validPositiveNumber.test(event.currentTarget.value)
    ) {
      return;
    }

    setValue(event.currentTarget.value);

    let percent;

    if (isLpPool) {
      percent = Math.ceil(
        (parseFloat(event.currentTarget.value) / parseFloat(lpBalance)) * 10000,
      );
    } else {
      percent = Math.ceil(
        (parseFloat(event.currentTarget.value) /
          parseFloat(starsBalance.data.formatted)) *
          10000,
      );
    }

    setPercentage(percent.toString());
  };

  const handlePercentage = (
    e: React.FormEvent<HTMLButtonElement>,
    bipAmount: string,
  ) => {
    e.preventDefault();

    if (isStarsLoading) {
      return;
    }

    let walletPortion;

    if (isLpPool) {
      if (bipAmount === '10000') {
        walletPortion = lpBalance;
      } else {
        walletPortion = (
          (parseFloat(lpBalance) * parseInt(bipAmount, 10)) /
          10000
        ).toString();
      }
    } else if (bipAmount === '10000') {
      walletPortion = starsBalance.data!.formatted;
    } else {
      walletPortion = (
        (parseFloat(starsBalance.data!.formatted) * parseInt(bipAmount, 10)) /
        10000
      ).toString();
    }

    setValue(walletPortion);
    setPercentage(bipAmount);
  };

  const handleStake = async (e: React.FormEvent<HTMLButtonElement>) => {
    e.preventDefault();

    if (!address || submitting) {
      return;
    }

    setSubmitting(true);

    try {
      const txn = await deposit(props.poolId, parseEther(value), address);

      if (!txn.success) {
        setSubmitting(false);
        return;
      }

      const url = `${explorerUrl}/tx/${txn.transactionHash}`;

      setTransactionUrl(url);
      setModalState(ModalState.finished);

      toast.success(`Staking Successsful!`);
    } catch (error) {
      setSubmitting(false);
      toast.error(`Staking Failed`);

      throw error;
    }

    setSubmitting(false);
  };

  useEffect(() => {
    if (!address || !starsInitialized || !LpInitialized) {
      return;
    }

    void (async () => {
      const [lpAllowed, allowed] = await Promise.all([
        lpAllowance(address, WEB3_CONFIG.contracts[chainKey].starsStaking),
        allowance(address, WEB3_CONFIG.contracts[chainKey].starsStaking),
      ]);

      if (!isLpPool && allowed !== '0') {
        setApproved(true);

        return;
      }

      if (lpAllowed !== '0') {
        setApproved(true);
      }

      setLpBalance(
        formatEther((await getBalance(address)) ?? BigNumber.from('0'))
          .toFixed(6)
          .toString(),
      );
    })();
  }, [starsInitialized, LpInitialized, address, chainKey]);

  const isStakeDisabled =
    (isStarsLoading || isLpPool
      ? parseFloat(lpBalance) < parseFloat(value)
      : parseFloat(starsBalance.data!.formatted) < parseFloat(value)) ||
    value === '0' ||
    value === '';

  if (modalState === ModalState.finished) {
    return (
      <>
        <Toaster toastOptions={{ success: { duration: 5000 } }} />
        <ModalFinishState
          header="PURCHASE COMPLETED"
          transactionUrl={transactionUrl}
          handleClose={handleClose}
        />
      </>
    );
  }

  return (
    <>
      <Toaster toastOptions={{ success: { duration: 5000 } }} />
      <div className={`${styles.modalBG} max-w-screen-sm lg:max-w-full`}>
        {submitting && (
          <div className={styles.starsLoading}>
            <CircularProgress className={`${classes.loader} mb-5`} size={85} />
            <div className={styles.description}>Awaiting Transaction</div>
          </div>
        )}
        <form autoComplete="off">
          <div className={styles.xIconBG} onClick={handleClose}>
            <XIcon className={styles.xIcon} />
          </div>
          <div>
            <div
              className={`${styles.title} fontVanguard font-bold text-center`}
            >
              {`${props.poolName}`}
            </div>
            <hr className={styles.titleHR} />

            <div className="text-left">
              <div
                className={styles.poolMonths}
              >{`${props.poolDuration} MONTH POOL`}</div>
              <div className={styles.poolInfoGeneral}>
                <div className="flex flex-row pt-5">
                  <div
                    className={styles.poolInfo}
                  >{`Minimum lockup period: `}</div>
                  <div className="ml-2">
                    {props.lockupPeriod > 1
                      ? `${props.lockupPeriod} Months`
                      : `${props.lockupPeriod} Month`}
                  </div>
                </div>
                <div className="flex flex-row">
                  <div
                    className={`${styles.poolInfo} self-center align-bottom`}
                  >
                    APR
                  </div>
                  <Tooltip
                    className={styles.poolInfo}
                    title="An annual or annualized return
is a measure of how much an investment
has increased on average each year."
                    placement="top"
                    arrow
                  >
                    <HelpOutlineIcon
                      className="ml-1 mr-2 opacity-50 place-self-center"
                      fontSize="small"
                    />
                  </Tooltip>{' '}
                  <div>{`${props.apr}%`}</div>
                </div>
              </div>
              <div className="pt-5">
                <div className="flex flex-row justify-between">
                  <div
                    className={`${styles.title} fontVanguard font-bold pb-4`}
                  >
                    AMOUNT
                  </div>
                </div>
                <div className={`${styles.inputOuter}`}>
                  <div
                    className={`${styles.inputInner} flex flex-row justify-between`}
                  >
                    <div className="flex flex-row content-center items-center">
                      <div className="flex justify-center items-start gap-2 relative text-center">
                        {isLpPool ? (
                          <img
                            height="30"
                            className={'self-center'}
                            src={bnbStakingLogo}
                            alt="bnbStakingLogo z-0"
                          />
                        ) : null}
                        <img
                          height="30"
                          className={
                            isLpPool
                              ? '-ml-6 z-10 self-center'
                              : 'z-10 self-center'
                          }
                          src={starsStakingLogo}
                          alt="starsStakingLogo"
                        />
                      </div>
                      <div
                        className={`${styles.tokenName} place-self-center font-bold`}
                      >
                        {isLpPool
                          ? `MOGUL-${getNetworkCurrencySymbol()}`
                          : 'MOGUL'}
                      </div>
                    </div>
                    <div
                      className={`${styles.inputFieldContainer} flex flex-row content-center place-self-center`}
                    >
                      <input
                        type="number"
                        value={parseFloat(value).toString()}
                        className={styles.inputFieldsOld}
                        onChange={handleStakingAmount}
                        disabled={isStarsLoading}
                      ></input>
                    </div>
                  </div>
                  <div className="flex lg:flex-row flex-col justify-between mt-3">
                    <div className="flex justify-between w-3/12">
                      <button
                        className={
                          percentage === '2500'
                            ? styles.active
                            : styles.inactive
                        }
                        onClick={e => handlePercentage(e, '2500')}
                      >
                        25%
                      </button>
                      <button
                        className={
                          percentage === '5000'
                            ? styles.active
                            : styles.inactive
                        }
                        onClick={e => handlePercentage(e, '5000')}
                      >
                        50%
                      </button>
                      <button
                        className={
                          percentage === '7500'
                            ? styles.active
                            : styles.inactive
                        }
                        onClick={e => handlePercentage(e, '7500')}
                      >
                        75%
                      </button>
                      <button
                        className={
                          percentage === '10000'
                            ? styles.active
                            : styles.inactive
                        }
                        onClick={e => handlePercentage(e, '10000')}
                      >
                        100%
                      </button>
                    </div>
                    {!isLpPool ? (
                      <div className="mt-3 lg:mt-0">
                        ~{' '}
                        {!isNaN(parseFloat(value) * starsUsdRate)
                          ? parseFloat(value) * starsUsdRate < 100000000000
                            ? (parseFloat(value) * starsUsdRate).toLocaleString(
                                'en-US',
                                {
                                  style: 'currency',
                                  currency: 'USD',
                                  minimumFractionDigits: 2,
                                  maximumFractionDigits: 2,
                                },
                              ) + ' USD'
                            : '$' +
                              (parseFloat(value) * starsUsdRate).toExponential(
                                4,
                              ) +
                              ' USD'
                          : '$0.00 USD'}
                      </div>
                    ) : (
                      <div className="mt-3 lg:mt-0">
                        ~{' '}
                        {!isNaN(
                          parseFloat(value) *
                            (props.lpUSD / props.lpTotalSupply),
                        )
                          ? parseFloat(value) *
                              (props.lpUSD / props.lpTotalSupply) <
                            100000000000
                            ? (
                                parseFloat(value) *
                                (props.lpUSD / props.lpTotalSupply)
                              ).toLocaleString('en-US', {
                                style: 'currency',
                                currency: 'USD',
                                minimumFractionDigits: 2,
                                maximumFractionDigits: 2,
                              }) + ' USD'
                            : '$' +
                              (
                                parseFloat(value) *
                                (props.lpUSD / props.lpTotalSupply)
                              ).toExponential(4) +
                              ' USD'
                          : '$0.00 USD'}
                      </div>
                    )}
                  </div>
                  <div
                    className={`${styles.inputOuterText} flex flex-col lg:flex-row justify-between pt-3`}
                  >
                    <div className="flex flex-row">
                      Balance:
                      {isLpPool ? (
                        <div className="ml-3">
                          {lpBalance} MOGUL-{getNetworkCurrencySymbol()}
                        </div>
                      ) : (
                        <div className="ml-3">
                          {starsBalance.data?.formatted ?? '0'} MOGUL
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              <div className="flex flex-col w-full justify-center text-center pt-10 pb-4">
                {approved ? (
                  <Button
                    type="submit"
                    fullWidth
                    disabled={isStakeDisabled}
                    onClick={handleStake}
                  >
                    STAKE
                  </Button>
                ) : (
                  <Button fullWidth onClick={handleApprove}>
                    {isLpPool
                      ? `APPROVE MOGUL-${getNetworkCurrencySymbol()}`
                      : 'APPROVE MOGUL'}
                  </Button>
                )}
              </div>
            </div>
          </div>
        </form>
      </div>
    </>
  );
};

export const createStakeModal =
  (props: StakingModalProps) =>
  ({ hideModal }: { hideModal: () => void }) => {
    return <StakingModal onClose={() => hideModal()} {...props} />;
  };
